import { EMPLOYEE_ROLE_LABELS, NO_ROLE_LABEL } from "@etudescarre/constants";
import { Button, Checkbox, Drawer, Select } from "@etudescarre/ui";
import { isNotNil } from "@etudescarre/utils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { fetchAllCompanies } from "./components";

// "none" = contact-only, no role assigned.
const NO_ROLE_VALUE = "none";

type EmployeeFormData = {
  companyId: number;
  email: string;
  firstName: string;
  lastName: string;
  role: "admin" | "user" | typeof NO_ROLE_VALUE;
  isActive: boolean;
  dateJoined?: string;
};

type Employee = {
  id: string;
  companyId: number;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  role: "user" | "admin" | "root" | null;
  isActive: boolean;
  dateJoined: string | null;
};

// A newly created collaborator's role is preselected from their email
// domain — Admin for @chantiercarre.fr, User otherwise — and stays
// editable afterwards.
const ADMIN_EMAIL_DOMAIN = "@chantiercarre.fr";
function defaultRoleForEmail(email: string): "admin" | "user" {
  return email.trim().toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN)
    ? "admin"
    : "user";
}

function roleFieldValue(
  role: Employee["role"] | undefined,
): "admin" | "user" | typeof NO_ROLE_VALUE {
  if (role === "admin" || role === "user") return role;
  return NO_ROLE_VALUE;
}

type Company = {
  id: number;
  name: string;
};

export type EmployeeFormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee?: Employee;
  onSuccess?: () => void;
};

export function EmployeeForm({
  open,
  onOpenChange,
  employee,
  onSuccess,
}: EmployeeFormProps) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const isEdit = !!employee;
  // Once the user manually picks a role, the email-domain default stops
  // overriding it. Always true when editing: the existing
  // role is never silently replaced by typing in the email field.
  const [roleTouched, setRoleTouched] = useState(isEdit);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<EmployeeFormData>({
    defaultValues: {
      companyId: employee?.companyId ?? 0,
      email: employee?.email ?? "",
      firstName: employee?.firstName ?? "",
      lastName: employee?.lastName ?? "",
      role: isEdit ? roleFieldValue(employee.role) : "user",
      isActive: employee?.isActive ?? true,
      dateJoined: employee?.dateJoined?.split("T")[0] ?? "",
    },
  });

  const isActive = watch("isActive");
  const companyId = watch("companyId");
  const role = watch("role");

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    const { companies } = await fetchAllCompanies();
    if (isNotNil(companies)) {
      setCompanies(
        companies.map((c) => ({
          id: c.id,
          name: c.name,
        })),
      );
    }
    setLoadingCompanies(false);
  }, []);

  useEffect(() => {
    if (open) {
      fetchCompanies();
      reset({
        companyId: employee?.companyId ?? 0,
        email: employee?.email ?? "",
        firstName: employee?.firstName ?? "",
        lastName: employee?.lastName ?? "",
        role: isEdit ? roleFieldValue(employee.role) : "user",
        isActive: employee?.isActive ?? true,
        dateJoined: employee?.dateJoined?.split("T")[0] ?? "",
      });
      setRoleTouched(isEdit);
    }
  }, [open, employee, isEdit, reset, fetchCompanies]);

  const onSubmit = async (data: EmployeeFormData) => {
    // Last name is always stored uppercase; first name is free-form.
    const lastName = data.lastName.toUpperCase();
    const role = data.role === NO_ROLE_VALUE ? null : data.role;

    try {
      if (isEdit) {
        const { error } = await apiClient.PATCH("/api/admin/employees/{id}", {
          params: { path: { id: employee.id } },
          body: {
            companyId: data.companyId,
            email: data.email || null,
            firstName: data.firstName,
            lastName,
            role: employee.role === "root" ? undefined : role,
            isActive: data.isActive,
            dateJoined: data.dateJoined
              ? new Date(data.dateJoined).toISOString()
              : null,
          },
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la modification de l'employé",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Employé modifié avec succès",
        });
      } else {
        const { error } = await apiClient.POST("/api/admin/employees", {
          body: {
            companyId: data.companyId,
            email: data.email || undefined,
            firstName: data.firstName,
            lastName,
            role: role ?? undefined,
            isActive: data.isActive,
            dateJoined: data.dateJoined
              ? new Date(data.dateJoined).toISOString()
              : undefined,
          },
        });

        if (error) {
          toaster.error({
            title: "Erreur",
            description: error.error || "Échec de la création de l'employé",
          });
          return;
        }

        toaster.success({
          title: "Succès",
          description: "Employé créé avec succès",
        });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    }
  };

  const companyOptions = companies.map((c) => ({
    label: c.name,
    value: String(c.id),
  }));

  const roleOptions = [
    { label: EMPLOYEE_ROLE_LABELS.user, value: "user" },
    { label: EMPLOYEE_ROLE_LABELS.admin, value: "admin" },
    { label: `${NO_ROLE_LABEL} (contact)`, value: NO_ROLE_VALUE },
  ];

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
      // A toast error (e.g. "Email invalide") renders outside the drawer;
      // without this, Ark treats clicking it as an outside interaction and
      // closes the drawer, losing the in-progress entry.
      // closeOnInteractOutside alone isn't enough in Safari — preventing
      // the event directly makes it browser-independent.
      closeOnInteractOutside={false}
      onInteractOutside={(e) => e.preventDefault()}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content>
            <div className="flex items-center justify-between border-b border-zinc-200 p-6 dark:border-zinc-800">
              <div>
                <Drawer.Title>
                  {isEdit ? "Modifier l'employé" : "Créer un employé"}
                </Drawer.Title>
                <Drawer.Description>
                  {isEdit
                    ? "Modifiez les informations de l'employé"
                    : "Créez un nouvel employé"}
                </Drawer.Description>
              </div>
              <Drawer.CloseTrigger />
            </div>

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
                {/* Company Select */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Société <span className="text-red-500">*</span>
                  </span>
                  {!loadingCompanies && companyOptions.length === 0 ? (
                    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800">
                      <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        Aucune société trouvée.
                      </p>
                      <Link
                        to="/admin/companies"
                        className="mt-1 inline-block text-sm text-blue-600 hover:underline dark:text-blue-400"
                      >
                        Créer une société
                      </Link>
                    </div>
                  ) : (
                    <Select
                      options={companyOptions}
                      value={companyId ? [String(companyId)] : []}
                      onValueChange={(details) => {
                        const val = details.value[0];
                        setValue("companyId", val ? Number(val) : 0);
                      }}
                      placeholder="Sélectionner une société"
                      disabled={loadingCompanies}
                      required
                    />
                  )}
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Email{" "}
                    {role !== NO_ROLE_VALUE && (
                      <span className="text-red-500">*</span>
                    )}
                  </label>
                  <input
                    id="email"
                    type="email"
                    {...register("email", {
                      onChange: (e) => {
                        if (!isEdit && !roleTouched) {
                          setValue("role", defaultRoleForEmail(e.target.value));
                        }
                      },
                    })}
                    required={role !== NO_ROLE_VALUE}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="email@example.com (facultatif)"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label
                    htmlFor="firstName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    {...register("firstName")}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Prénom"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label
                    htmlFor="lastName"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    {...register("lastName", {
                      // Last name is always stored uppercase; first name is
                      // free-form.
                      onChange: (e) =>
                        setValue("lastName", e.target.value.toUpperCase(), {
                          shouldValidate: true,
                        }),
                    })}
                    required
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                    placeholder="Nom"
                  />
                </div>

                {/* Role Select */}
                <div>
                  <span className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Rôle <span className="text-red-500">*</span>
                  </span>
                  <Select
                    options={roleOptions}
                    value={[role]}
                    onValueChange={(details) => {
                      const val = details.value[0];
                      setRoleTouched(true);
                      setValue(
                        "role",
                        val === "admin" || val === "user" ? val : NO_ROLE_VALUE,
                      );
                    }}
                    placeholder="Sélectionner un rôle"
                  />
                </div>

                {/* Date Joined */}
                <div>
                  <label
                    htmlFor="dateJoined"
                    className="mb-1 block text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Date d'embauche
                  </label>
                  <input
                    id="dateJoined"
                    type="date"
                    {...register("dateJoined")}
                    className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                  />
                </div>

                {/* Active Checkbox */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={isActive}
                    onCheckedChange={(details) =>
                      setValue("isActive", details.checked === true)
                    }
                  />
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Actif
                  </span>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex shrink-0 justify-end gap-3 border-t border-zinc-200 p-6 dark:border-zinc-800">
                <Button
                  type="button"
                  outline
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Annuler
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "En cours..." : isEdit ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
